<template>
  <div class="quote-container">
    <StepWizard
      v-model="currentStepNumber"
      class="quote-container__steps section"
      :class="stepClasses"
      :steps="steps"
      hide-title
      @returned="returnToStart"
      @completed="goToSuccessPage"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import StepWizard from "@/components/StepWizard.vue"

import { Page } from "@/router/types"
import { mapState } from "vuex"
import { RootState } from "@/store/untested/types"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"

const LEGACY_ORDER = [
  Page.EnterPostcode,
  Page.SelectAddress,
  Page.CustomerCheck,
  Page.QuoteCustomerLogin,
  Page.QuoteCustomerDetails,
  Page.QuoteNewPostcode,
  Page.QuoteNewAddress,
  Page.FindYourRoof,
  Page.ExtraInfo,
  Page.QuoteSolarPackages,
  Page.QuoteElectricityUsage,
  Page.QuoteElectricityUsageEstimate,
  Page.QuoteElectricityUnitRate,
  Page.WhenAtHome,
  Page.WhenGetSolar,
  Page.ConservationArea,
  Page.ContactDetails,
]

const RECKONER_ORDER = [
  Page.EnterPostcode,
  Page.SelectAddress,
  Page.CustomerCheck,
  Page.QuoteCustomerLogin,
  Page.QuoteCustomerDetails,
  Page.QuoteNewPostcode,
  Page.QuoteNewAddress,
  Page.FindYourRoof,
  Page.QuotePropertyType,
  Page.QuoteElectricityUsage,
  Page.QuoteElectricityUsageEstimate,
  Page.QuoteSolarEstimate,
  Page.QuoteSolarPackages,
  Page.ExtraInfo,
  Page.QuoteElectricityUnitRate,
  Page.WhenAtHome,
  Page.QuoteIntent,
  Page.ConservationArea,
  Page.ContactDetails,
]

export default defineComponent({
  components: {
    StepWizard,
  },
  data: () => ({
    currentStepNumber: null,
    newBranding: process.env.VUE_APP_NEW_BRANDING === "true",
    quoteReckoner: process.env.VUE_APP_ENABLE_QUOTE_RECKONER === "true",
  }),
  computed: {
    ...mapState({
      selectedPackage: (state: RootState) => state.quote.solarPackage,
    }),
    stepClasses() {
      return {
        "full-width":
          this.steps[this.currentStepNumber ? this.currentStepNumber : 0]
            .fullWidth,
      }
    },
    stepConfig(): Record<string, { skip?: boolean; fullWidth?: boolean }> {
      const quote = this.$store.state.quote
      const isLoggedIn = this.$store.getters.isLoggedIn
      const addressFromApi = !quote.overrideAddress
      return {
        [Page.EnterPostcode]: { skip: isLoggedIn || !this.newBranding },
        [Page.SelectAddress]: { skip: isLoggedIn },
        [Page.CustomerCheck]: { skip: isLoggedIn },
        [Page.QuoteCustomerLogin]: { skip: !quote.isExistingCustomer },
        [Page.QuoteCustomerDetails]: {
          skip: !quote.isExistingCustomer && !isLoggedIn,
        },
        [Page.QuoteNewPostcode]: { skip: !quote.overrideAddress },
        [Page.QuoteNewAddress]: { skip: !quote.overrideAddress },
        [Page.FindYourRoof]: { fullWidth: true },
        [Page.ExtraInfo]: { fullWidth: true },
        [Page.QuoteSolarPackages]: { fullWidth: true },
        [Page.QuoteSolarEstimate]: { fullWidth: true },
        [Page.QuoteElectricityUsage]: {
          skip: quote.estimatedUsage.fromApi && addressFromApi,
        },
        [Page.QuoteElectricityUsageEstimate]: {
          skip:
            quote.isUsageExact === true ||
            (quote.estimatedUsage.fromApi && addressFromApi),
        },
        [Page.QuoteElectricityUnitRate]: {
          skip: quote.unitRate.fromApi && addressFromApi,
        },
      }
    },
    steps() {
      const order = this.quoteReckoner ? RECKONER_ORDER : LEGACY_ORDER
      return order.map((routeName) => ({
        routeName,
        ...this.stepConfig[routeName],
      }))
    },
  },
  mounted() {
    TrackingService.sendEvent(
      `Solar:isLoggedUser:${this.$store.getters.isLoggedIn}`
    )
  },
  methods: {
    returnToStart() {
      if (this.newBranding) {
        window.location.assign(`${process.env.VUE_APP_WEBSITE_URL}/solar`)
      } else {
        this.$router.push("/")
      }
    },
    goToSuccessPage() {
      this.$router.push({
        name: Page.ThankYou,
        query: { covered: "true" },
        params: {
          email: this.$store.state.quote.email,
        },
      })
      this.$store.commit("RESET_STATE")
    },
  },
})
</script>

<style lang="scss" scoped>
.quote-container {
  background-color: $grey-50;
  flex: 1;
  &__steps {
    padding-top: 0;

    @include sm {
      padding: 0 $space-4 $space-6 $space-4;
    }

    @include md {
      padding: 0 0 $space-6 0;
      margin: 0 auto;
      width: $width-4;
    }
  }

  &__steps.full-width {
    @include md {
      width: fit-content;
      max-width: calc(100% - (#{$space-6} * 2));
    }
  }
}
</style>
